import {useState, useEffect} from 'react';
import './occupation-input.scss';
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextFieldPropsColorOverrides,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import {OccupationsRepository} from '../../repositories/occupations';
import {OverridableStringUnion} from '@mui/types';
import {UserType} from '../../backend/enums/user-type';
import {
  FlatSelectDropdown,
  Option as DropdownOption,
} from '../flat-select-dropdown/flat-select-dropdown';

export interface OccupationOption {
  occupationId: string;
  occupationDesignation: string;
}

interface OccupationInputProps {
  label: string;
  handleChange: (value?: OccupationOption) => void;
  handleBlur?: () => void;
  error?: string;
  required?: boolean;
  value?: OccupationOption;
  disabled?: boolean;
  disableClear?: boolean;
  disabledTooltip?: string;
  className?: string;
  id?: string;
  multiline?: boolean;
  color?: OverridableStringUnion<
    'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning',
    TextFieldPropsColorOverrides
  >;
  variant?: 'outlined';
  userType: UserType;
}

/**
 * Occupation search field.
 *
 * In the DB, the occupations have 4 levels:
 * 1) Major group
 * 2) Sub major group
 * 3) Minor group
 * 4) Unit group
 * 1, 2, and 3 are not selectable occupations. Only occupations from 4 can be selected by the user
 * as valid occupations. Thus, 4 are our options in the flat dropdown, and 1, 2, and 3 are the levels
 * we use to group our occupations.
 */
export const OccupationInput = ({
  id,
  className,
  label,
  value,
  color,
  error,
  required,
  disabled,
  disableClear = false,
  disabledTooltip,
  handleChange,
  handleBlur,
  variant,
  userType,
}: OccupationInputProps) => {
  // Hold state
  const [options, setOptions] = useState<Array<DropdownOption>>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch occupations
  const handleFetchOccupations = async () => {
    try {
      // Start loading
      setLoading(true);

      // Get occupations
      const response = await OccupationsRepository.instance.get('', true, userType);

      // Process response
      const occupationOptions: Array<DropdownOption> = response
        .filter((option) => option.id)
        .map((option) => ({
          key: option.id!,
          label: option.designation,
        }));

      // Set occupations
      setOptions(occupationOptions);
    } catch (error: any) {
      // Log
      console.debug('🔴 Error while fetching occupations.', error);

      // Set empty values
      setOptions([]);
    } finally {
      // Stop loading
      setLoading(false);
    }
  };

  // Load occupations once on mount or when the user type changes
  useEffect(() => {
    handleFetchOccupations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType]);

  // Handle option
  const handleOption = (selectedOption: DropdownOption | null) => {
    if (!selectedOption || selectedOption.key === '') {
      handleChange(undefined);
      return;
    }

    handleChange({
      occupationId: selectedOption.key,
      occupationDesignation: selectedOption.label,
    });
  };

  const selectedValue = value ? value.occupationId : '';
  const showClearButton = !!selectedValue && disableClear === false && !disabled;

  if (variant === 'outlined') {
    return (
      <Box sx={{width: '100%'}} className={className}>
        <FormControl fullWidth variant="outlined" error={!!error}>
          <InputLabel id={`${id || 'occupation'}-label`} shrink>
            {required ? `${label} *` : label}
          </InputLabel>
          <Select
            labelId={`${id || 'occupation'}-label`}
            id={id}
            value={selectedValue}
            label={required ? `${label} *` : label}
            onChange={(event: SelectChangeEvent) => {
              const nextValue = event.target.value;
              if (!nextValue) {
                handleOption(null);
                return;
              }

              const option = options.find((item) => item.key === nextValue);
              handleOption(option ?? null);
            }}
            onBlur={handleBlur}
            disabled={disabled}
            displayEmpty
            sx={{
              width: '100%',
              '& .MuiSelect-select': {
                paddingRight: showClearButton ? '48px !important' : undefined,
              },
            }}
          >
            {options.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {showClearButton && (
            <IconButton
              onClick={() => handleOption(null)}
              sx={{
                position: 'absolute',
                right: '32px',
                top: '14px',
                width: '28px',
                height: '28px',
              }}
              aria-label="clear selection"
            >
              <ClearIcon sx={{fontSize: '20px'}} />
            </IconButton>
          )}
        </FormControl>
      </Box>
    );
  }

  return (
    <FlatSelectDropdown
      id={id}
      // className={className}
      label={label}
      options={options}
      value={value ? {key: value.occupationId, label: value.occupationDesignation} : undefined}
      onChange={handleOption}
      onBlur={handleBlur}
      loading={loading}
      disabled={disabled}
      clearable={!disableClear}
      disabledTooltip={disabledTooltip}
      errors={error}
      required={required}
      placeholder={label}
      color={color}
      sx={{
        width: '100%',
      }}
    />
  );
};
